/**
 * PhaseRegistry - Zentrale Definition aller Spielphasen
 *
 * Definiert alle Phasen und ihre erlaubten Übergänge
 * als State Machine.
 */

/**
 * Alle verfügbaren Spielphasen
 */
export const PHASES = {
  // === Meta-Phasen ===
  INTRO: 'intro',              // Intro-Screen
  PROFILE_SETUP: 'profileSetup', // Nickname-Setup für neue User
  START_SCREEN: 'startScreen', // Haupt-Hub nach Login (Stammtisch-Übersicht)
  STAMMTISCH_SETUP: 'stammtischSetup', // Neuen Stammtisch erstellen
  STAMMTISCH: 'stammtisch',    // Stammtisch-Fullscreen (Private Room Lobby)
  GAME_MODE_SELECT: 'gameModeSelect', // Spielmodus-Auswahl (für Public Games)
  QUICK_PLAY: 'quickPlay',    // Quick Play Tabbed Lobby (Public Games)
  LOBBY: 'lobby',              // Raum/Spieler-Verwaltung

  // === Pre-Game ===
  PRE_GAME_SHOP: 'preGameShop', // Shop vor erster Runde (modular)

  // === Hauptspiel-Loop ===
  PLAYING: 'playing',          // Karten spielen (inkl. Exchange-Subphase!)
  ROUND_END: 'roundEnd',       // Rundenende-Anzeige
  EXCHANGE: 'exchanging',      // @deprecated - Exchange ist jetzt Sub-Phase von PLAYING
  SHOP: 'shop',                // Shop zwischen Runden

  // === Spielende ===
  VICTORY: 'victory',          // Siegerehrung (neu)

  // === Victory-Subphasen ===
  VICTORY_OVERVIEW: 'victoryOverview',
  VICTORY_STATISTICS: 'victoryStatistics',
  VICTORY_AWARDS: 'victoryAwards',
  VICTORY_FINAL: 'victoryFinal',

  // === Sonderzustände ===
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
};

/**
 * Erlaubte Phasenübergänge (State Machine)
 */
export const PHASE_TRANSITIONS = {
  // Intro führt zu ProfileSetup (wenn kein Nickname), StartScreen, Stammtisch (bei Invite-Link), oder Lobby
  [PHASES.INTRO]: [PHASES.PROFILE_SETUP, PHASES.START_SCREEN, PHASES.STAMMTISCH, PHASES.LOBBY],

  // ProfileSetup führt zu StartScreen nach Nickname-Eingabe, oder zu Stammtisch bei Invite-Link
  [PHASES.PROFILE_SETUP]: [PHASES.START_SCREEN, PHASES.STAMMTISCH],

  // StartScreen führt zu QuickPlay, GameModeSelect (legacy), StammtischSetup (Erstellen), Stammtisch (öffnen), oder Lobby (direkter Join)
  [PHASES.START_SCREEN]: [PHASES.QUICK_PLAY, PHASES.GAME_MODE_SELECT, PHASES.STAMMTISCH_SETUP, PHASES.STAMMTISCH, PHASES.LOBBY],

  // StammtischSetup führt zu Stammtisch nach Erstellung oder zurück zu StartScreen
  [PHASES.STAMMTISCH_SETUP]: [PHASES.STAMMTISCH, PHASES.START_SCREEN],

  // Stammtisch ist die private Room Lobby mit Journey-Visualisierung
  [PHASES.STAMMTISCH]: [PHASES.PRE_GAME_SHOP, PHASES.PLAYING, PHASES.START_SCREEN],

  // Spielmodus-Auswahl führt zur Lobby (für Public Games - legacy)
  [PHASES.GAME_MODE_SELECT]: [PHASES.LOBBY],

  // Quick Play kann zu Pre-Game-Shop, Spielen, oder zurück zum StartScreen
  [PHASES.QUICK_PLAY]: [PHASES.PRE_GAME_SHOP, PHASES.PLAYING, PHASES.START_SCREEN],

  // Lobby kann zu Pre-Game-Shop, Spielen, oder zurück zum StartScreen führen
  [PHASES.LOBBY]: [PHASES.PRE_GAME_SHOP, PHASES.PLAYING, PHASES.START_SCREEN],

  // Pre-Game-Shop führt zum Spielen oder zurück zum Lobby-Ursprung
  [PHASES.PRE_GAME_SHOP]: [PHASES.PLAYING, PHASES.LOBBY, PHASES.QUICK_PLAY, PHASES.STAMMTISCH, PHASES.START_SCREEN],

  // Spielen kann zu Rundenende, Sieg oder Hauptmenü (Spiel verlassen) führen
  [PHASES.PLAYING]: [PHASES.ROUND_END, PHASES.VICTORY, PHASES.START_SCREEN],

  // Rundenende führt zum Shop, Sieg oder Hauptmenü (Spiel verlassen)
  [PHASES.ROUND_END]: [PHASES.SHOP, PHASES.VICTORY, PHASES.START_SCREEN],

  // @deprecated - Austausch führt zum Shop (für Rückwärtskompatibilität)
  [PHASES.EXCHANGE]: [PHASES.SHOP],

  // Shop führt zurück zum Spielen oder zum Lobby-Ursprung
  [PHASES.SHOP]: [PHASES.PLAYING, PHASES.LOBBY, PHASES.QUICK_PLAY, PHASES.STAMMTISCH, PHASES.START_SCREEN],

  // Victory-Flow
  [PHASES.VICTORY]: [PHASES.VICTORY_OVERVIEW],
  [PHASES.VICTORY_OVERVIEW]: [PHASES.VICTORY_STATISTICS],
  [PHASES.VICTORY_STATISTICS]: [PHASES.VICTORY_AWARDS],
  [PHASES.VICTORY_AWARDS]: [PHASES.VICTORY_FINAL],
  [PHASES.VICTORY_FINAL]: [PHASES.LOBBY, PHASES.QUICK_PLAY, PHASES.STAMMTISCH, PHASES.START_SCREEN, PHASES.GAME_MODE_SELECT],

  // Sonderzustände können überall hin
  [PHASES.DISCONNECTED]: Object.values(PHASES),
  [PHASES.ERROR]: [PHASES.LOBBY, PHASES.QUICK_PLAY, PHASES.STAMMTISCH, PHASES.START_SCREEN, PHASES.INTRO],
};

/**
 * Phasen-Metadaten
 */
export const PHASE_META = {
  [PHASES.INTRO]: {
    name: 'Intro',
    description: 'Willkommen-Bildschirm',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
    featureFlag: 'INTRO_SCREEN',
  },
  [PHASES.PROFILE_SETUP]: {
    name: 'Profil',
    description: 'Wähle deinen Nickname',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: false,
  },
  [PHASES.START_SCREEN]: {
    name: 'Start',
    description: 'Dein Spielmenü',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
  },
  [PHASES.STAMMTISCH_SETUP]: {
    name: 'Neuer Stammtisch',
    description: 'Erstelle einen neuen Stammtisch',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
  },
  [PHASES.STAMMTISCH]: {
    name: 'Stammtisch',
    description: 'Dein privater Spieltisch',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
  },
  [PHASES.GAME_MODE_SELECT]: {
    name: 'Spielmodus',
    description: 'Wähle deinen Spielmodus',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
  },
  [PHASES.QUICK_PLAY]: {
    name: 'Schnelles Spiel',
    description: 'Erstelle oder tritt einem Raum bei',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
  },
  [PHASES.LOBBY]: {
    name: 'Lobby',
    description: 'Spielersuche und Einstellungen',
    showHeader: true,
    showPlayerBar: false,
    allowLeave: true,
  },
  [PHASES.PRE_GAME_SHOP]: {
    name: 'Start-Shop',
    description: 'Kaufe Joker vor dem Spiel',
    showHeader: true,
    showPlayerBar: true,
    allowLeave: false,
    featureFlag: 'PRE_GAME_SHOP',
    timeout: 60000, // 60 Sekunden Standard
  },
  [PHASES.PLAYING]: {
    name: 'Spielen',
    description: 'Karten ausspielen',
    showHeader: true,
    showPlayerBar: true,
    allowLeave: false,
  },
  [PHASES.ROUND_END]: {
    name: 'Rundenende',
    description: 'Ergebnisse der Runde',
    showHeader: true,
    showPlayerBar: true,
    allowLeave: false,
    // No autoAdvance - user must click "Weiter" which calls socketService.proceedToShop()
  },
  [PHASES.EXCHANGE]: {
    name: 'Kartentausch',
    description: 'Kartentausch-Phase (Legacy)',
    showHeader: true,
    showPlayerBar: true,
    allowLeave: false,
    featureFlag: 'CARD_EXCHANGE',
    renderedBy: 'PlayingPhase', // Exchange is now a sub-phase of PlayingPhase
  },
  [PHASES.SHOP]: {
    name: 'Shop',
    description: 'Kaufe Joker und Siegel',
    showHeader: true,
    showPlayerBar: true,
    allowLeave: false,
    timeout: 60000,
  },
  // ============================================
  // Victory Phases
  // ============================================
  // ARCHITECTURE NOTE: Victory is now rendered by RoundEndPhase component using
  // VICTORY_SLIDES configuration (see src/components/splash/slideConfigs.js).
  // These phase definitions are kept for:
  // - Server-side phase transition logic
  // - PhaseContext state tracking
  // - Backwards compatibility
  // The PhaseManager maps all victory phases to RoundEndPhase component.
  [PHASES.VICTORY]: {
    name: 'Siegerehrung',
    description: 'Das Spiel ist vorbei!',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
    featureFlag: 'VICTORY_CEREMONY',
    renderedBy: 'RoundEndPhase', // Clarity: which component renders this
  },
  [PHASES.VICTORY_OVERVIEW]: {
    name: 'Übersicht',
    description: 'Gewinner und Verlierer',
    showHeader: false,
    showPlayerBar: false,
    autoAdvance: true,
    autoAdvanceDelay: 5000,
    renderedBy: 'RoundEndPhase',
  },
  [PHASES.VICTORY_STATISTICS]: {
    name: 'Statistiken',
    description: 'Spielstatistiken',
    showHeader: false,
    showPlayerBar: false,
    autoAdvance: true,
    autoAdvanceDelay: 8000,
    renderedBy: 'RoundEndPhase',
  },
  [PHASES.VICTORY_AWARDS]: {
    name: 'Auszeichnungen',
    description: 'Besondere Leistungen',
    showHeader: false,
    showPlayerBar: false,
    autoAdvance: true,
    autoAdvanceDelay: 6000,
    renderedBy: 'RoundEndPhase',
  },
  [PHASES.VICTORY_FINAL]: {
    name: 'Endergebnis',
    description: 'Finale Platzierungen',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
    renderedBy: 'RoundEndPhase',
  },
  [PHASES.DISCONNECTED]: {
    name: 'Verbindung unterbrochen',
    description: 'Versuche neu zu verbinden...',
    showHeader: false,
    showPlayerBar: false,
  },
  [PHASES.ERROR]: {
    name: 'Fehler',
    description: 'Ein Fehler ist aufgetreten',
    showHeader: false,
    showPlayerBar: false,
    allowLeave: true,
  },
};

/**
 * Hauptspiel-Phasen (Loop)
 * Note: EXCHANGE ist nicht mehr dabei - es ist jetzt Sub-Phase von PLAYING
 */
export const MAIN_GAME_PHASES = [
  PHASES.PLAYING,
  PHASES.ROUND_END,
  PHASES.SHOP,
];

/**
 * Victory-Subphasen
 * NOTE: Victory is now rendered by RoundEndPhase with VICTORY_SLIDES.
 * These phase IDs are kept for server transition logic and PhaseContext tracking.
 */
export const VICTORY_SUBPHASES = [
  PHASES.VICTORY_OVERVIEW,
  PHASES.VICTORY_STATISTICS,
  PHASES.VICTORY_AWARDS,
  PHASES.VICTORY_FINAL,
];

/**
 * Prüft ob ein Übergang erlaubt ist
 */
export function canTransition(fromPhase, toPhase) {
  // INTRO ist immer erreichbar (für Logout)
  if (toPhase === PHASES.INTRO) {
    return true;
  }
  const allowed = PHASE_TRANSITIONS[fromPhase] || [];
  return allowed.includes(toPhase);
}

/**
 * Gibt die nächste Phase im Standard-Flow zurück
 */
export function getNextPhase(currentPhase, context = {}) {
  const {
    roundComplete = false,
    gameComplete = false,
    skipPreGameShop = false,
  } = context;

  switch (currentPhase) {
    case PHASES.INTRO:
      return PHASES.START_SCREEN;

    case PHASES.START_SCREEN:
      return PHASES.GAME_MODE_SELECT;

    case PHASES.GAME_MODE_SELECT:
      return PHASES.LOBBY;

    case PHASES.LOBBY:
      return skipPreGameShop ? PHASES.PLAYING : PHASES.PRE_GAME_SHOP;

    case PHASES.PRE_GAME_SHOP:
      return PHASES.PLAYING;

    case PHASES.PLAYING:
      return roundComplete
        ? (gameComplete ? PHASES.VICTORY : PHASES.ROUND_END)
        : PHASES.PLAYING;

    case PHASES.ROUND_END:
      // Exchange ist jetzt Sub-Phase von Playing - gehe direkt zu Shop
      return gameComplete ? PHASES.VICTORY : PHASES.SHOP;

    case PHASES.EXCHANGE:
      // @deprecated - für Rückwärtskompatibilität
      return PHASES.SHOP;

    case PHASES.SHOP:
      return PHASES.PLAYING;

    case PHASES.VICTORY:
      return PHASES.VICTORY_OVERVIEW;

    case PHASES.VICTORY_OVERVIEW:
      return PHASES.VICTORY_STATISTICS;

    case PHASES.VICTORY_STATISTICS:
      return PHASES.VICTORY_AWARDS;

    case PHASES.VICTORY_AWARDS:
      return PHASES.VICTORY_FINAL;

    case PHASES.VICTORY_FINAL:
      return PHASES.LOBBY;

    default:
      return currentPhase;
  }
}

/**
 * Prüft ob Phase eine Victory-Subphase ist
 */
export function isVictoryPhase(phase) {
  return VICTORY_SUBPHASES.includes(phase) || phase === PHASES.VICTORY;
}

/**
 * Prüft ob Phase eine Hauptspiel-Phase ist
 */
export function isMainGamePhase(phase) {
  return MAIN_GAME_PHASES.includes(phase);
}

/**
 * Gibt Phase-Metadaten zurück
 */
export function getPhaseMeta(phase) {
  return PHASE_META[phase] || {
    name: phase,
    description: '',
    showHeader: true,
    showPlayerBar: true,
  };
}

export default PHASES;
